import React, { useEffect, useRef, useState } from "react"
import { AdminUser } from "../../models/user"
import { useForm } from "@mantine/form"
import { updateUserPassword } from "../api"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, PasswordInput, ScrollArea, Stack } from "@mantine/core"
import { confirmDiscardChanges } from "../utils/confirm"

interface Props {
  opened: boolean
  onClose: () => void
  clientId: number
  user: AdminUser
  isSelf: boolean
  onSaved?: () => void
}

interface FormValues {
  current_password: string
  password: string
  password_confirmation: string
}

const defaultValues: FormValues = {
  current_password: '',
  password: '',
  password_confirmation: '',
}

export const UserPasswordUpdateModal: React.FC<Props> = ({
  opened, onClose, clientId, user, isSelf, onSaved,
}) => {
  const [submitting, setSubmitting] = useState(false)
  const isSelfRef = useRef(isSelf)

  useEffect(() => { isSelfRef.current = isSelf }, [isSelf])

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      current_password: (v) => (isSelfRef.current && !v ? '必須です' : null),
      password: (v) => {
        if (!v) return '必須です'
        if (v.length < 8 || v.length > 16) return '8～16文字で入力してください'
        return null
      },
      password_confirmation: (v, values) => {
        if (!v) return '必須です'
        if (v !== values.password) return 'パスワードが一致しません'
        return null
      },
    },
  })

  useEffect(() => {
    if (opened) {
      form.setValues(defaultValues)
      form.resetDirty()
    }
  }, [opened, user])

  const handleClose = () => {
    if (form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)

    try {
      await updateUserPassword(
        clientId,
        user.id,
        values.current_password,
        values.password,
        values.password_confirmation,
      )
      notifications.show({ message: 'パスワードを変更しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (errors: any) {
      const message = Array.isArray(errors) ? errors.join('\n') : 'パスワードの変更に失敗しました'
      notifications.show({ message, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="パスワード変更"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            {isSelf && (
              <PasswordInput
                label={<><span>現在のパスワード</span>{requiredMark}</>}
                autoComplete="current-password"
                {...form.getInputProps('current_password')}
                styles={requireLabelStyles}
              />
            )}
            <PasswordInput
              label={<><span>新しいパスワード</span>{requiredMark}</>}
              placeholder="8～16文字"
              autoComplete="new-password"
              {...form.getInputProps('password')}
              styles={requireLabelStyles}
            />
            <PasswordInput
              label={<><span>新しいパスワード（確認）</span>{requiredMark}</>}
              placeholder="8～16文字"
              autoComplete="new-password"
              {...form.getInputProps('password_confirmation')}
              styles={requireLabelStyles}
            />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              パスワードを変更する
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}
